"use client"

import { useState, useEffect } from "react"
import { TabGroup, TabGroupList, TabGroupTrigger, TabGroupContent } from "@/components/ui/tab-group"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { DashboardLayout } from "@/components/dashboard/dashboard-layout"
import { useAuth } from "@/src/contexts/AuthContext"
import { 
  Copy, 
  RefreshCw, 
  Eye, 
  EyeOff, 
  Trash2, 
  Plus, 
  Shield,
  Clock,
  AlertCircle,
  CheckCircle2,
  Lock
} from "lucide-react"
import { toast } from "sonner"
import {
  listApiKeys,
  revealApiKey,
  regenerateApiKey,
  getIpWhitelist,
  addIpToWhitelist,
  removeIpFromWhitelist,
  type ApiKey,
  type RevealedKey
} from "@/lib/api/apiKeys"

export default function ApiAuthPage() {
  const { hasPermission } = useAuth()
  const canManage = hasPermission('api:manage')
  
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([])
  const [loading, setLoading] = useState(true)
  const [revealedKeys, setRevealedKeys] = useState<Record<string, RevealedKey>>({})
  const [ipWhitelist, setIpWhitelist] = useState<string[]>([])
  const [newIpAddress, setNewIpAddress] = useState("")
  const [selectedKeyId, setSelectedKeyId] = useState<string | null>(null)
  const [countdown, setCountdown] = useState<Record<string, number>>({})

  // Load API keys on mount
  useEffect(() => {
    loadApiKeys()
  }, [])

  // Countdown timer for revealed keys
  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown(prev => {
        const newCountdown: Record<string, number> = {}
        Object.entries(prev).forEach(([keyId, seconds]) => {
          if (seconds > 0) {
            newCountdown[keyId] = seconds - 1
          } else {
            // Auto-hide key when countdown reaches 0
            setRevealedKeys(prevRevealed => {
              const updated = { ...prevRevealed }
              delete updated[keyId]
              return updated
            })
          }
        })
        return newCountdown
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  const loadApiKeys = async () => {
    try {
      setLoading(true)
      const response = await listApiKeys()
      setApiKeys(response)
      
      // Select first key by default
      if (response.length > 0) {
        setSelectedKeyId(response[0].id)
        loadIpWhitelist(response[0].id)
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to load API keys")
    } finally {
      setLoading(false)
    }
  }

  const loadIpWhitelist = async (keyId: string) => {
    try {
      const response = await getIpWhitelist(keyId)
      setIpWhitelist(response.ip_whitelist)
    } catch (error: any) {
      toast.error(error.message || "Failed to load IP whitelist")
    }
  }

  const handleRevealKey = async (keyId: string) => {
    try {
      const response = await revealApiKey(keyId)
      setRevealedKeys(prev => ({
        ...prev,
        [keyId]: response
      }))
      
      // Set countdown timer (5 minutes = 300 seconds)
      setCountdown(prev => ({
        ...prev,
        [keyId]: 300
      }))
      
      toast.success("API key revealed successfully")
    } catch (error: any) {
      if (error.message.includes("Rate limit exceeded")) {
        toast.error("คุณเปิดดู key มากเกินไป (สูงสุด 10 ครั้ง/ชั่วโมง)")
      } else {
        toast.error(error.message || "Failed to reveal API key")
      }
    }
  }

  const handleHideKey = (keyId: string) => {
    setRevealedKeys(prev => {
      const updated = { ...prev }
      delete updated[keyId]
      return updated
    })
    setCountdown(prev => {
      const updated = { ...prev }
      delete updated[keyId]
      return updated
    })
  }

  const handleRegenerateKey = async (keyId: string) => {
    if (!confirm("⚠️ คุณแน่ใจหรือไม่?\n\n• API key เดิมจะถูกปิดใช้งานทันทีและไม่สามารถใช้งานได้อีก\n• ระบบอนุญาตให้มี 1 API key ต่อ 1 บัญชีเท่านั้น\n• API key ใหม่สามารถใช้ได้กับทุก package ที่มีสิทธิ์ใช้ API")) {
      return
    }

    try {
      const response = await regenerateApiKey(keyId)
      
      // Show new key
      setRevealedKeys(prev => ({
        ...prev,
        [response.id]: {
          key: response.key,
          key_id: response.key_id,
          reveal_count: 0,
          revealed_at: new Date().toISOString(),
          hide_after: new Date(Date.now() + 300000).toISOString()
        }
      }))
      
      setCountdown(prev => ({
        ...prev,
        [response.id]: 300
      }))
      
      // Reload list
      await loadApiKeys()
      
      toast.success("API key regenerated successfully", {
        description: "กรุณาบันทึก key ใหม่ จะไม่แสดงอีกครั้ง"
      })
    } catch (error: any) {
      toast.error(error.message || "Failed to regenerate API key")
    }
  }

  const handleCopyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success(`${label} คัดลอกแล้ว`)
    } catch (error) {
      toast.error("Failed to copy")
    }
  }

  const handleAddIpAddress = async () => {
    if (!selectedKeyId) {
      toast.error("Please select an API key first")
      return
    }

    if (!newIpAddress.trim()) {
      toast.error("Please enter an IP address")
      return
    }

    try {
      await addIpToWhitelist(selectedKeyId, newIpAddress.trim())
      await loadIpWhitelist(selectedKeyId)
      setNewIpAddress("")
      toast.success("IP address added successfully")
    } catch (error: any) {
      toast.error(error.message || "Failed to add IP address")
    }
  }

  const handleDeleteIpAddress = async (ip: string) => {
    if (!selectedKeyId) return

    try {
      await removeIpFromWhitelist(selectedKeyId, ip)
      await loadIpWhitelist(selectedKeyId)
      toast.success("IP address removed successfully")
    } catch (error: any) {
      toast.error(error.message || "Failed to remove IP address")
    }
  }

  const formatCountdown = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${minutes}:${secs.toString().padStart(2, '0')}`
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-brand-blue" />
            <p className="text-sm lg:text-base text-gray-600">Loading API keys...</p>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4 lg:mb-6">
          <Shield className="w-6 h-6 lg:w-7 lg:h-7 text-brand-blue" />
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900">API Keys</h1>
        </div>

        {/* Tabs */}
        <TabGroup defaultValue="api-keys" size="lg" color="brand-blue" fullWidth>
          <TabGroupList className="mb-4 lg:mb-6">
            <TabGroupTrigger value="api-keys">API Keys</TabGroupTrigger>
            <TabGroupTrigger value="ip-whitelist">IP Whitelist</TabGroupTrigger>
          </TabGroupList>

          {/* API Keys Tab Content */}
          <TabGroupContent value="api-keys">
            <div className="space-y-4 lg:space-y-6">
              {/* Info Banner */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">📌 นโยบาย API Key</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>ระบบอนุญาตให้มี <strong>1 API key ต่อ 1 บัญชี</strong> เท่านั้น</li>
                    <li>เมื่อ Regenerate จะสร้าง key ใหม่และปิดใช้งาน key เดิมทันที</li>
                    <li>API key <strong>ไม่ผูกกับ package</strong> - เปลี่ยน package ได้โดยไม่ต้องสร้าง key ใหม่</li>
                    <li>สามารถใช้ API key เดิมได้ทันทีหาก package ใหม่มีสิทธิ์ใช้ API</li>
                  </ul>
                </div>
              </div>

              {apiKeys.map((apiKey) => {
                const revealed = revealedKeys[apiKey.id]
                const timeLeft = countdown[apiKey.id]

                return (
                  <Card key={apiKey.id} className="border border-gray-200">
                    <CardContent className="p-4 lg:p-6 space-y-4">
                      {/* Header */}
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="space-y-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="text-base lg:text-lg font-semibold text-gray-900">{apiKey.name}</h3>
                            {apiKey.is_active ? (
                              <Badge className="bg-green-500 text-xs">Active</Badge>
                            ) : (
                              <Badge variant="secondary" className="text-xs">Inactive</Badge>
                            )}
                            {/* Removed package_level badge - package is checked from customer account */}
                          </div>
                          {apiKey.description && (
                            <p className="text-xs lg:text-sm text-gray-600">{apiKey.description}</p>
                          )}
                        </div>
                      </div>

                      {/* API Key Display */}
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <label className="text-xs lg:text-sm font-medium text-gray-700">API Key</label>
                          <div className="flex flex-wrap items-center gap-2 text-xs lg:text-sm text-gray-500">
                            {apiKey.reveal_count > 0 && (
                              <span className="flex items-center gap-1">
                                <Eye className="w-3 h-3 lg:w-4 lg:h-4" />
                                {apiKey.reveal_count} times
                              </span>
                            )}
                            {apiKey.last_revealed_at && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3 lg:w-4 lg:h-4" />
                                {new Date(apiKey.last_revealed_at).toLocaleDateString('th-TH')}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-2">
                          <div className="flex-1 relative">
                            <Input
                              type="text"
                              value={revealed ? revealed.key : apiKey.key_id_masked}
                              readOnly
                              className={`font-mono text-xs lg:text-sm ${revealed ? 'text-green-600' : 'text-gray-500'} pr-12`}
                            />
                            <div className="absolute right-2 top-1/2 -translate-y-1/2">
                              {revealed && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleCopyToClipboard(revealed.key, "API Key")}
                                  className="h-7 w-7 lg:h-8 lg:w-8 p-0"
                                >
                                  <Copy className="w-3 h-3 lg:w-4 lg:h-4" />
                                </Button>
                              )}
                            </div>
                          </div>

                          <div className="flex gap-2">
                            {apiKey.can_reveal && !revealed && (
                              <Button
                                onClick={() => handleRevealKey(apiKey.id)}
                                variant="outline"
                                size="sm"
                                className="gap-1 lg:gap-2 text-xs lg:text-sm"
                              >
                                <Eye className="w-3 h-3 lg:w-4 lg:h-4" />
                                <span className="hidden sm:inline">Reveal</span>
                              </Button>
                            )}

                            {revealed && (
                              <Button
                                onClick={() => handleHideKey(apiKey.id)}
                                variant="outline"
                                size="sm"
                                className="gap-1 lg:gap-2 text-xs lg:text-sm"
                              >
                                <EyeOff className="w-3 h-3 lg:w-4 lg:h-4" />
                                <span className="hidden sm:inline">Hide</span>
                              </Button>
                            )}

                            <Button
                              onClick={() => handleRegenerateKey(apiKey.id)}
                              variant="destructive"
                              size="sm"
                              disabled={!canManage}
                              className="gap-1 lg:gap-2 text-xs lg:text-sm"
                              title={!canManage ? "Enterprise package required" : ""}
                            >
                              {!canManage && <Lock className="w-3 h-3 lg:w-4 lg:h-4" />}
                              <RefreshCw className="w-3 h-3 lg:w-4 lg:h-4" />
                              <span className="hidden sm:inline">Regenerate</span>
                            </Button>
                          </div>
                        </div>
                        
                        {/* Enterprise Feature Badge */}
                        {!canManage && (
                          <div className="flex items-center gap-2 text-xs text-orange-600 bg-orange-50 px-3 py-2 rounded-lg">
                            <Lock className="w-3 h-3 flex-shrink-0" />
                            <span>Regenerate API Key เป็นฟีเจอร์สำหรับ <strong>Enterprise Package</strong> เท่านั้น</span>
                          </div>
                        )}

                        {/* Countdown Timer */}
                        {revealed && timeLeft !== undefined && (
                          <div className="flex items-center gap-2 text-xs lg:text-sm">
                            <AlertCircle className="w-3 h-3 lg:w-4 lg:h-4 text-orange-500" />
                            <span className="text-orange-600">
                              Key will auto-hide in {formatCountdown(timeLeft)}
                            </span>
                          </div>
                        )}

                        {/* Warning for revealed key */}
                        {revealed && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 lg:w-5 lg:h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                            <div className="text-xs lg:text-sm text-yellow-800">
                              <p className="font-medium">🔐 กรุณาบันทึก API key นี้ไว้อย่างปลอดภัย</p>
                              <ul className="mt-1 list-disc list-inside space-y-0.5">
                                <li>คุณจะไม่สามารถเห็น key นี้อีกครั้ง หลังจากหมดเวลา หรือกด Hide</li>
                                <li>ใช้ปุ่ม "Reveal" เพื่อดู key อีกครั้ง (สูงสุด 10 ครั้ง/ชั่วโมง)</li>
                                <li>API key นี้ใช้ได้กับทุก package ที่มีสิทธิ์ใช้ API Connect</li>
                              </ul>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Metadata */}
                      <div className="border-t border-gray-200 pt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs lg:text-sm">
                        <div>
                          <span className="text-gray-600">Created:</span>{" "}
                          <span className="text-gray-900">{new Date(apiKey.created_at).toLocaleDateString('th-TH')}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Last Used:</span>{" "}
                          <span className="text-gray-900">
                            {apiKey.last_used ? new Date(apiKey.last_used).toLocaleDateString('th-TH') : 'Never'}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )
              })}

              {/* Usage Examples Section */}
              <div className="space-y-4 lg:space-y-6">
                <div className="border-t border-gray-200 pt-4 lg:pt-6">
                  <h2 className="text-base lg:text-lg font-bold text-gray-900 mb-4">ตัวอย่างการใช้งาน</h2>
                </div>

                {/* JavaScript Example */}
                <Card className="border border-gray-200">
                  <CardContent className="p-0">
                    <div className="flex items-center justify-between p-3 lg:p-4 bg-gray-50 border-b border-gray-200">
                      <h3 className="text-sm lg:text-base font-semibold text-gray-900">JavaScript</h3>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleCopyToClipboard(
                          `const axios = require('axios')

let config = {
  method: 'POST',
  url: 'https://api.thaislipcheck.com/api/verify-slip/qr-code/info',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY_HERE',
    'Content-Type': 'application/json'
  },
  data: {
    payload: {
      qrCode: "00410006000000402TH9104xxxx"
    }
  }
};

axios.request(config)
  .then((response) => {
    console.log(JSON.stringify(response.data))
  })
  .catch((error) => {
    console.log(error)
  })`,
                          "JavaScript code"
                        )}
                        className="gap-2"
                      >
                        <Copy className="w-3 h-3 lg:w-4 lg:h-4" />
                        <span className="text-xs lg:text-sm">Copy</span>
                      </Button>
                    </div>
                    <div className="p-4 lg:p-6 bg-gray-900">
                      <pre className="text-xs lg:text-sm text-green-400 overflow-x-auto">
                        <code>{`const axios = require('axios')

let config = {
  method: 'POST',
  url: 'https://api.thaislipcheck.com/api/verify-slip/qr-code/info',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY_HERE',
    'Content-Type': 'application/json'
  },
  data: {
    payload: {
      qrCode: "00410006000000402TH9104xxxx"
    }
  }
};

axios.request(config)
  .then((response) => {
    console.log(JSON.stringify(response.data))
  })
  .catch((error) => {
    console.log(error)
  })`}</code>
                      </pre>
                    </div>
                  </CardContent>
                </Card>

                {/* Curl Example */}
                <Card className="border border-gray-200">
                  <CardContent className="p-0">
                    <div className="flex items-center justify-between p-3 lg:p-4 bg-gray-50 border-b border-gray-200">
                      <h3 className="text-sm lg:text-base font-semibold text-gray-900">cURL</h3>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleCopyToClipboard(
                          `curl -iL -X POST 'https://api.thaislipcheck.com/api/verify-slip/qr-code/info' \\
  -H 'Content-Type: application/json' \\
  -H 'Authorization: Bearer YOUR_API_KEY_HERE' \\
  --json '{
    "payload": {
      "qrCode": "00410006000000402TH9104xxxx"
    }
  }'`,
                          "cURL code"
                        )}
                        className="gap-2"
                      >
                        <Copy className="w-3 h-3 lg:w-4 lg:h-4" />
                        <span className="text-xs lg:text-sm">Copy</span>
                      </Button>
                    </div>
                    <div className="p-4 lg:p-6 bg-gray-900">
                      <pre className="text-xs lg:text-sm text-green-400 overflow-x-auto">
                        <code>{`curl -iL -X POST 'https://api.thaislipcheck.com/api/verify-slip/qr-code/info' \\
  -H 'Content-Type: application/json' \\
  -H 'Authorization: Bearer YOUR_API_KEY_HERE' \\
  --json '{
    "payload": {
      "qrCode": "00410006000000402TH9104xxxx"
    }
  }'`}</code>
                      </pre>
                    </div>
                  </CardContent>
                </Card>

                {/* Usage Instructions */}
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="p-4 lg:p-6">
                    <div className="flex gap-3">
                      <AlertCircle className="w-4 h-4 lg:w-5 lg:h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="text-xs lg:text-sm text-amber-900 space-y-2">
                        <p className="font-medium">วิธีใช้งาน:</p>
                        <ol className="list-decimal list-inside space-y-1 ml-2">
                          <li>แทนที่ <code className="bg-amber-100 px-1.5 py-0.5 rounded text-xs">YOUR_API_KEY_HERE</code> ด้วย API key ของคุณ</li>
                          <li>เพิ่ม Header <code className="bg-amber-100 px-1.5 py-0.5 rounded text-xs">Authorization: Bearer [API_KEY]</code></li>
                          <li>ส่ง request ไปยัง API endpoint ที่ต้องการ</li>
                        </ol>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Info Section */}
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4 lg:p-6">
                  <div className="flex gap-3">
                    <AlertCircle className="w-4 h-4 lg:w-5 lg:h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-xs lg:text-sm text-blue-900 space-y-2">
                      <p className="font-medium">ข้อควรระวัง:</p>
                      <ul className="list-disc list-inside space-y-1 ml-2">
                        <li>Reveal API key สูงสุด 10 ครั้งต่อชั่วโมง</li>
                        <li>API key จะซ่อนอัตโนมัติหลังจาก 5 นาที</li>
                        <li>การ Regenerate จะทำให้ key เดิมใช้งานไม่ได้ทันที</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabGroupContent>

          {/* IP Whitelist Tab Content */}
          <TabGroupContent value="ip-whitelist">
            <div className="space-y-4 lg:space-y-6">
              {/* Instructions */}
              <Card className="bg-white">
                <CardContent className="p-4 lg:p-6">
                  <h2 className="text-base lg:text-lg font-bold text-gray-900 mb-3 lg:mb-4">
                    การกำหนด IP Whitelist
                  </h2>
                  <ol className="space-y-2 text-xs lg:text-sm text-gray-600">
                    <li>
                      <span className="font-medium">1.</span> กำหนด IP Whitelist เพื่อระบุ IP Address ที่สามารถใช้งาน API ได้
                    </li>
                    <li>
                      <span className="font-medium">2.</span> สามารถกำหนดได้สูงสุด{" "}
                      <span className="font-semibold">10 IP Address</span>
                    </li>
                    <li>
                      <span className="font-medium">3.</span> IP Whitelist ว่างหมายถึง{" "}
                      <span className="font-semibold">รับทุก IP Address</span>
                    </li>
                  </ol>
                </CardContent>
              </Card>

              {/* API Key Selection */}
              {apiKeys.length > 1 && (
                <div className="space-y-2">
                  <label className="text-xs lg:text-sm font-medium text-gray-700">เลือก API Key</label>
                  <div className="flex flex-wrap gap-2">
                    {apiKeys.map((key) => (
                      <Button
                        key={key.id}
                        size="sm"
                        variant={selectedKeyId === key.id ? "default" : "outline"}
                        onClick={() => {
                          setSelectedKeyId(key.id)
                          loadIpWhitelist(key.id)
                        }}
                        className="text-xs lg:text-sm"
                      >
                        {key.name}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {selectedKeyId && (
                <Card className="bg-white">
                  <CardContent className="p-4 lg:p-6 space-y-4">
                    {/* Counter */}
                    <div className="flex items-center justify-between">
                      <h3 className="text-base lg:text-lg font-bold text-gray-900">
                        IP Address ({ipWhitelist.length}/10)
                      </h3>
                    </div>

                    {/* Add IP Form */}
                    <div className="space-y-2">
                      <label htmlFor="ip-address" className="block text-xs lg:text-sm text-brand-blue font-medium">
                        เพิ่ม IP Address<span className="text-red-500">*</span>
                      </label>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Input
                          id="ip-address"
                          type="text"
                          placeholder="เพิ่ม IP Address (เช่น 203.0.113.42)"
                          value={newIpAddress}
                          onChange={(e) => setNewIpAddress(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleAddIpAddress()
                            }
                          }}
                          className="flex-1 text-xs lg:text-sm"
                        />
                        <Button
                          onClick={handleAddIpAddress}
                          variant="outline"
                          size="sm"
                          disabled={!canManage || ipWhitelist.length >= 10}
                          className="border-brand-blue text-brand-blue hover:bg-brand-blue/10 gap-1 lg:gap-2 text-xs lg:text-sm"
                          title={!canManage ? "Enterprise package required" : ""}
                        >
                          {!canManage && <Lock className="w-3 h-3 lg:w-4 lg:h-4" />}
                          <Plus className="w-3 h-3 lg:w-4 lg:h-4" />
                          เพิ่ม IP
                        </Button>
                      </div>
                      {!canManage && (
                        <p className="text-xs text-orange-600 flex items-center gap-1">
                          <Lock className="w-3 h-3" />
                          <span>จัดการ IP Whitelist เป็นฟีเจอร์สำหรับ <strong>Enterprise Package</strong> เท่านั้น</span>
                        </p>
                      )}
                    </div>

                    {/* IP List */}
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      {ipWhitelist.length === 0 ? (
                        <div className="p-4 lg:p-6 text-center text-gray-500 flex items-center justify-center gap-2">
                          <CheckCircle2 className="w-4 h-4 lg:w-5 lg:h-5 text-green-500" />
                          <span className="text-xs lg:text-sm">ไม่มีข้อจำกัด - รับทุก IP Address</span>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 lg:px-6 py-2 lg:py-3 text-left text-xs lg:text-sm font-bold text-gray-900">
                                  IP Address
                                </th>
                                <th className="px-3 lg:px-6 py-2 lg:py-3 text-right text-xs lg:text-sm font-bold text-gray-900">
                                  Actions
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {ipWhitelist.map((ip) => (
                                <tr key={ip} className="hover:bg-gray-50">
                                  <td className="px-3 lg:px-6 py-2 lg:py-3 text-xs lg:text-sm font-mono text-gray-900">
                                    {ip}
                                  </td>
                                  <td className="px-3 lg:px-6 py-2 lg:py-3 text-right">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleDeleteIpAddress(ip)}
                                      disabled={!canManage}
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 w-7 lg:h-8 lg:w-8 p-0 disabled:opacity-50"
                                      title={!canManage ? "Enterprise package required" : ""}
                                    >
                                      {!canManage ? <Lock className="w-3 h-3 lg:w-4 lg:h-4" /> : <Trash2 className="w-3 h-3 lg:w-4 lg:h-4" />}
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Info Section */}
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4 lg:p-6">
                  <div className="flex gap-3">
                    <AlertCircle className="w-4 h-4 lg:w-5 lg:h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-xs lg:text-sm text-blue-900 space-y-2">
                      <p className="font-medium">หมายเหตุ:</p>
                      <ul className="list-disc list-inside space-y-1 ml-2">
                        <li>IP Whitelist ว่างหมายถึง รับทุก IP Address</li>
                        <li>สามารถกำหนด IP Whitelist ได้สูงสุด 10 รายการ</li>
                        <li>ระบบจะตรวจสอบ IP address ทุกครั้งที่มีการเรียกใช้ API</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabGroupContent>
        </TabGroup>
      </div>
    </DashboardLayout>
  )
}
